import { AfterViewInit, Component, TemplateRef, ViewChild, inject, signal, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { UpdatePageTitle } from '../../../../store/app/app.actions';
import { AppTableComponent } from '../../../../shared/components/table/app-table.component';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';
import { RecurringFilterComponent } from '../recurring-topups/recurring-filter/recurring-filter.component';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TableColumn } from '@swimlane/ngx-datatable';
import { HttpParams } from '@angular/common/http';
import { scheduledTopupTableColumns } from '../../../../_services/topup-service/card-topups-utils';
import { TopUpSearchOptions } from '../../../../shared/components/forms/search-field/search-utils';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { CardTopupsApiService } from '../../../../_services/topup-service/api/card-topups-api.service';
import { Observable } from 'rxjs';
import { PaginatedResults } from '@bo/ng-api';
import { CardScheduleStatus, CardTopup } from '../../../../_interfaces/card-topups.interface';
import { updateParamsObject } from '../../../../shared/utils/filter-utils';
import { TopUpFrequency } from '../../../../_interfaces/employee.interface';
import {
  StatusBadgeComponent,
  statusBadgeDepartments,
} from '../../../../shared/components/status-badge/status-badge.component';
import { CardStatus, CardType } from '../../../../_interfaces/cards.interface';
import { TableHeaderTemplateComponent } from '../../../../shared/components/table-header-template/table-header-template.component';
import { CardTopupsBalanceWidget } from '../_components/card-topups-balance-widget/card-topups-balance-widget';
import { CardNumberDisplayComponent } from '../../../../shared/components/card-number-display/card-number-display.component';

@Component({
  selector: 'app-scheduled-top-ups',
  imports: [
    AppTableComponent,
    CurrencyPipe,
    EmptyIndicatorPipe,
    RecurringFilterComponent,
    RouterOutlet,
    DatePipe,
    StatusBadgeComponent,
    TableHeaderTemplateComponent,
    CardTopupsBalanceWidget,
    CardNumberDisplayComponent,
  ],
  templateUrl: './scheduled-topups.component.html',
})
export class ScheduledTopupsComponent implements OnInit, AfterViewInit {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly offcanvasService = inject(OffcanvasService);
  readonly cardTopupsApiService = inject(CardTopupsApiService);

  columns: Array<TableColumn> = [];
  queryParams = new HttpParams();
  protected readonly TopUpFrequency = TopUpFrequency;
  protected readonly topUpSearchOptions = TopUpSearchOptions;
  protected readonly CardScheduleStatus = CardScheduleStatus;
  protected readonly statusBadgeDepartments = statusBadgeDepartments;
  protected readonly CardType = CardType;
  private readonly columnsInitialized = signal(false);

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // TODO: TemplateRef<any> can be fixed by refactoring the @bo/ng-datatable library.
  @ViewChild('cardNumberCell', { static: false }) cardNumberCell!: TemplateRef<any>;
  @ViewChild('cardNameCell', { static: false }) cardNameCell!: TemplateRef<any>;
  @ViewChild('cardHolderCell', { static: false }) cardHolderCell!: TemplateRef<any>;
  @ViewChild('periodCell', { static: false }) periodCell!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCell!: TemplateRef<any>;
  @ViewChild('amountCell', { static: false }) amountCell!: TemplateRef<any>;
  @ViewChild('actionsCell', { static: false }) actionsCell!: TemplateRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  constructor() {
    this.store.dispatch(new UpdatePageTitle('Scheduled Top-ups'));
  }

  ngOnInit() {
    // Initialise columns early to prevent expression changed error
    this.columns = scheduledTopupTableColumns;
  }

  ngAfterViewInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    if (this.columnsInitialized()) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const templateMap: Record<string, TemplateRef<any> | undefined> = {
      'card.masked_number': this.cardNumberCell,
      'card.name': this.cardNameCell,
      'employee.first_name': this.cardHolderCell,
      'date_start': this.periodCell,
      'schedule_status': this.statusCell,
      'amount': this.amountCell,
      'actions': this.actionsCell,
    };

    // Set templates on columns
    for (const column of this.columns) {
      if (!column.prop) continue; // Skip if prop is undefined
      const template = templateMap[column.prop];
      if (template) {
        column.cellTemplate = template;
      }
    }

    // Reassign array to trigger change detection in the table
    this.columns = [...this.columns];
    this.columnsInitialized.set(true);
  }

  getData(params: HttpParams): Observable<PaginatedResults<CardTopup>> {
    return this.cardTopupsApiService.getScheduledTopups(params);
  }

  update(params: HttpParams) {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: updateParamsObject(params),
      })
      .then(() => {});
  }

  onCreate() {
    this.offcanvasService.offCanvasNavigate(`/card-topups/scheduled/add`);
  }

  editRule(id: number) {
    this.offcanvasService.offCanvasNavigate(`/card-topups/scheduled/edit/${id}`);
  }

  protected readonly CardStatus = CardStatus;
}
